import type { ProposalSectionMessage, ProposalSseResponse } from '@/api/proposals/proposals'
import { ProposalSseType } from '@/api/proposals/proposals'
import { mapProposalErrorMessage } from './errorMessages'

export interface ProposalStreamState {
  streamingBySectionId: Record<number, string>
  activeSectionId: number | null
  statusMessage: string | null
  error: string | null
  progressSections: Array<ProposalDraftingProgressSection>
  // Advisory warning frames (SseTypeWarning), keyed by section id. Unlike statusMessage these are
  // never cleared by a content frame or by resetForStreamClose (see below): they are emitted after
  // a section's body has already streamed and must remain visible on that section until ITS OWN
  // next build/refine/regenerate starts. useProposalStream's start() scopes the clear to the
  // section it is about to (re)build (see clearWarningsForSection below), not the whole map, so
  // refining one section does not erase another section's still-relevant warning.
  warningsBySectionId: Record<number, Array<string>>
}

export interface ProposalDraftingProgressSection {
  sectionId: number
  heading: string
  message: string
}

export const initialProposalStreamState: ProposalStreamState = {
  streamingBySectionId: {},
  activeSectionId: null,
  statusMessage: null,
  error: null,
  progressSections: [],
  warningsBySectionId: {}
}

// resetForStreamClose clears streaming buffers when a stream closes but PRESERVES any in-band
// error frame, so a build/refine failure survives the invalidate-on-close and reaches the UI. It
// also preserves per-section warnings for the same reason: they are advisory frames meant to
// persist on the section after its stream has closed, not transient progress text.
export function resetForStreamClose(state: ProposalStreamState): ProposalStreamState {
  return { ...initialProposalStreamState, error: state.error, warningsBySectionId: state.warningsBySectionId }
}

// clearWarningsForSection drops one section's accumulated warnings while leaving every other
// section's untouched. Used when a new refine/regenerate starts for that specific section: its own
// prior warnings no longer describe the draft about to be replaced, but a sibling section's warning
// is still describing its own, unchanged draft and must survive.
export function clearWarningsForSection(
  warnings: Record<number, Array<string>>,
  sectionId: number
): Record<number, Array<string>> {
  if (!(sectionId in warnings)) return warnings
  const next = { ...warnings }
  delete next[sectionId]
  return next
}

// isDraftingProgressFrame reports whether a status frame is one this list knows how to label: the
// "Drafting <heading>" frame each rewrite section starts with. Any other section-scoped status
// frame (e.g. "Regenerating section", or a frame this list does not otherwise recognise) carries no
// heading of its own to show.
function isDraftingProgressFrame(response: string): boolean {
  return /^Drafting\s+/i.test(response)
}

function headingFromProgressStatus(response: string): string {
  const heading = response.replace(/^Drafting\s+/i, '').trim()
  return heading.length > 0 ? heading : response
}

function upsertProgressSection(
  sections: Array<ProposalDraftingProgressSection>,
  event: ProposalSseResponse
): Array<ProposalDraftingProgressSection> {
  const existingIndex = sections.findIndex((section) => section.sectionId === event.sectionId)
  const existing = existingIndex === -1 ? undefined : sections[existingIndex]
  const recognised = isDraftingProgressFrame(event.response)
  if (!recognised && !existing) {
    // Neither a frame this list knows how to label, nor an existing entry whose heading it could
    // leave alone: showing the raw frame text (e.g. "Regenerating section") as a heading would be
    // worse than showing nothing, so skip it.
    return sections
  }
  const next = {
    sectionId: event.sectionId,
    heading: recognised ? headingFromProgressStatus(event.response) : (existing?.heading ?? event.response),
    message: event.response
  }
  if (existingIndex === -1) return [...sections, next]
  return sections.map((section, index) => (index === existingIndex ? next : section))
}

// applyProposalSseEvent folds one parsed proposal SSE message into the stream state. Pure, so the
// transport (start/close/error) stays in the hook and this is unit-tested directly. Section-level
// events (sectionId !== 0) update the active section; proposal-level events (sectionId 0, e.g. the
// terminal "done" status) leave it.
export function applyProposalSseEvent(state: ProposalStreamState, event: ProposalSseResponse): ProposalStreamState {
  switch (event.sseType) {
    case ProposalSseType.Content: {
      const prev = state.streamingBySectionId[event.sectionId] ?? ''
      return {
        ...state,
        statusMessage: null,
        activeSectionId: event.sectionId !== 0 ? event.sectionId : state.activeSectionId,
        streamingBySectionId: { ...state.streamingBySectionId, [event.sectionId]: prev + event.response }
      }
    }
    case ProposalSseType.Status:
      return {
        ...state,
        statusMessage: event.response,
        activeSectionId: event.sectionId !== 0 ? event.sectionId : state.activeSectionId,
        progressSections:
          event.sectionId !== 0 ? upsertProgressSection(state.progressSections, event) : state.progressSections
      }
    case ProposalSseType.Warning: {
      // Deliberately does not touch statusMessage or progressSections: a warning is emitted after
      // a section's body has already streamed and must not resurrect the pre-first-token indicator
      // or be mistaken for drafting progress (see upsertProgressSection).
      if (event.sectionId === 0) return state
      const prev = state.warningsBySectionId[event.sectionId] ?? []
      return {
        ...state,
        warningsBySectionId: { ...state.warningsBySectionId, [event.sectionId]: [...prev, event.response] }
      }
    }
    case ProposalSseType.Error:
      // mapProposalErrorMessage only rewords the one chat-shaped remediation clause that does not
      // apply here (see its doc comment); every other message passes through unchanged.
      return { ...state, error: mapProposalErrorMessage(event.response).message }
    default:
      return state
  }
}

// Local turns use negative ids so they never collide with server message ids and are trivially
// distinguishable. nowMs is passed in (not read from Date.now) to keep these pure and testable.
export function makeOptimisticUserTurn(content: string, nowMs: number): ProposalSectionMessage {
  return { messageId: -nowMs, author: 'user', content, createdOnTsMs: nowMs }
}

export function makeStreamedAssistantTurn(content: string, nowMs: number): ProposalSectionMessage {
  return { messageId: -(nowMs + 1), author: 'assistant', content, createdOnTsMs: nowMs }
}

// mergeSectionTranscript shows the persisted transcript plus any local turns not yet reflected by a
// refetch. Dedupe is by content: during the brief window where the refetch has landed but the local
// buffer has not yet cleared, the persisted turn already covers the local one, so we skip it.
export function mergeSectionTranscript(
  server: Array<ProposalSectionMessage>,
  optimisticUser: ProposalSectionMessage | null,
  streamedAssistant: ProposalSectionMessage | null
): Array<ProposalSectionMessage> {
  const seen = new Set(server.map((m) => m.content))
  const out = [...server]
  for (const turn of [optimisticUser, streamedAssistant]) {
    if (turn && !seen.has(turn.content)) {
      out.push(turn)
      seen.add(turn.content)
    }
  }
  return out
}
